/**
 * Scoped Deck API — `sync.decks.deck(name)` returns this.
 *
 * Provides a clean, name-scoped interface for card and property operations.
 * Card mutations are O(1) — they update a single card in the normalized
 * `cards: Record<deckName, Record<cardId, CardProps>>` store.
 *
 * Usage:
 * ```ts
 * const d = sync.decks.deck("math-101");
 *
 * // Reads (React hooks)
 * const cards = d.cards.use();          // CardProps[] (sorted)
 * const map = d.cards.useRecord();      // Record<string, CardProps> (raw)
 * const card = d.cards.useFind(cardId); // Per-card reactive — only THIS card
 * const props = d.properties.use();
 *
 * // Mutations (sync-safe, O(1))
 * d.cards.add({ front: "Q", back: "A" });
 * d.cards.edit(cardId, { front: "updated" });
 * d.cards.delete(cardId);
 * d.cards.star(cardId, true);
 * ```
 *
 * The implementation is split across `cards.ts`, `properties.ts`,
 * `notes.ts`, `sg-notes.ts`, plus shared `helpers.ts` and `types.ts`.
 * Each operation surface is its own file.
 */

import type { DeckId } from '@flashka/shared/sync-engine/schema/shared-types';

import { createCardsApi } from './cards';
import { store } from './helpers';
import { createNotesApi } from './notes';
import { createPropertiesApi } from './properties';
import type { ScopedDeck } from './types';

export { deleteDeckNotes } from './helpers';
export type { NoteReadResult } from './note-read';
export { createSgScopedNotes } from './sg-notes';
export type {
  CardEditable,
  NewCard,
  ScopedCards,
  ScopedDeck,
  ScopedNotes,
  ScopedProperties,
} from './types';

export function createScopedDeck(deckId: DeckId): ScopedDeck {
  const cards = createCardsApi(deckId);
  const properties = createPropertiesApi(deckId);
  const notes = createNotesApi(deckId);

  return {
    id: deckId,
    get deckPaths() {
      return store().deckProperties[deckId]?.deckPaths ?? [];
    },
    get displayName() {
      return store().deckProperties[deckId]?.displayName;
    },
    cards,
    properties,
    notes,
    remove: () => {
      // Tear down the per-deck Yjs sub-map first, while deckProperties is
      // still available for `resolveYjsDeckKey` to resolve the deckPath.
      // (`store().removeDeck()` soft-deletes — properties survive — but
      // ordering Yjs first matches the pre-refactor deck-slice behaviour
      // and keeps the cascade semantics obvious here at the top level.)
      notes.deleteAll();
      store().removeDeck(deckId);
    },
    moveToFolder: (folderId) => store().moveDeckToFolder(deckId, folderId),
    reorder: (sortKey) => store().reorderDeck(deckId, sortKey),
  };
}
